import React, { useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import supabase from '../../supabaseClient';

const findShipmentByReference = async (value) => {
  const normalizedValue = String(value || '').trim().toUpperCase();

  if (!normalizedValue) {
    return { data: null, error: null };
  }

  return supabase
    .from('shipments')
    .select('tracking_no, job_order, master_waybill, house_waybill')
    .or(
      `tracking_no.eq.${normalizedValue},job_order.eq.${normalizedValue},master_waybill.eq.${normalizedValue},house_waybill.eq.${normalizedValue}`
    )
    .limit(1)
    .maybeSingle();
};

const TrackAndTrace = () => {
  const [query, setQuery] = useState('');
  const [showFAQ, setShowFAQ] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const faqs = [
    {
      question: "How can I track my order?",
      answer:
        "After you place your order, you'll receive a tracking number, master waybill, or house waybill by email or SMS. Enter any of them here to view every scan, location, and status update as your package moves toward you.",
    },
    {
      question: "I haven't received my tracking number. What should I do?",
      answer:
        "If you don't see your tracking number, master waybill, or house waybill in your inbox or spam folder, please contact our support team. We'll verify your order and send you the correct reference right away.",
    },
    {
      question: "How long does delivery usually take?",
      answer:
        "Delivery times vary by shipping method and destination. Once you enter your tracking number, master waybill, or house waybill, you'll see an estimated delivery date based on the current transit progress.",
    },
    {
      question: "What if my package is delayed or lost?",
      answer:
        "If our system shows a delay or no updates for more than 48 hours, reach out to support. We'll investigate with the carrier and provide you with the next steps.",
    },
  ];

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    const trimmed = query.trim();
    const normalizedQuery = trimmed.toUpperCase();

    if (!trimmed) {
      toast.error('Please enter a tracking number, master waybill, or house waybill.', {
        position: 'top-right',
        style: { margin: '0 auto', maxWidth: '90vw' },
      });
      return;
    }

    setIsLoading(true);
    const MIN_LOAD_TIME = 1500;
    const start = Date.now();

    try {
      const { data, error } = await findShipmentByReference(normalizedQuery);

      const delay = Math.max(0, MIN_LOAD_TIME - (Date.now() - start));
      setTimeout(() => {
        if (error || !data) {
          toast.error('Reference not found. Please check the tracking number, master waybill, or house waybill and try again.', {
            position: 'top-right',
            style: { margin: '0 auto', maxWidth: '90vw' },
          });
        } else {
          navigate(`/tracking/${data.tracking_no}`);
        }
        setIsLoading(false);
      }, delay);
    } catch (err) {
      const delay = Math.max(0, MIN_LOAD_TIME - (Date.now() - start));
      setTimeout(() => {
        console.error(err);
        toast.error('Search failed. Please try again.', {
          position: 'top-right',
          style: { margin: '0 auto', maxWidth: '90vw' },
        });
        setIsLoading(false);
      }, delay);
    }
  };

  const openFAQ = () => {
    setShowFAQ(true);
    setActiveIndex(null);
  };

  const closeFAQ = () => {
    setShowFAQ(false);
    setActiveIndex(null);
  };

  const toggleFAQ = (i) => {
    setActiveIndex(i === activeIndex ? null : i);
  };

  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/20">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-blue/20 border-t-blue rounded-full animate-spin"></div>
                <div className="w-16 h-16 border-4 border-transparent border-t-yellow absolute inset-0 animate-ping"></div>
              </div>
              <div className="text-blue font-semibold text-lg">Searching for your package...</div>
              <div className="text-grey text-sm">Please wait while we locate your shipment</div>
            </div>
          </div>
        </div>
      )}

      <section
        id="track"
        className="relative min-h-screen flex flex-col justify-center font-poppins bg-white overflow-hidden"
      >
        {/* BG decor */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-72 h-72 bg-blue/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-yellow/5 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]  rounded-full blur-3xl"></div>
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 py-12">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-4 mb-6">
             
              <h1 className="text-5xl sm:text-6xl md:text-5xl font-bold text-blue">
                TRACK & TRACE
              </h1>
            </div>
            <p className="text-grey text-lg sm:text-xl font-medium max-w-2xl mx-auto">
              Monitor your shipment&apos;s journey from pickup to delivery with real-time updates using your tracking number, master waybill, or house waybill
            </p>
          </div>

          {/* GRID: 1 col on mobile, 2 cols on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch mb-8">
            {/* 1) Track Your Shipment (Search) – always first on mobile */}
            <div className="order-1 lg:order-1">
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 md:p-10 h-full flex flex-col">
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-3xl font-bold text-blue mb-2">Track Your Shipment</h2>
                  <p className="text-grey font-medium">Enter your tracking number, master waybill, or house waybill to get real-time updates</p>
                </div>

                <form onSubmit={handleSearch} className="space-y-6 flex-1">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
                      <i className="fas fa-barcode text-blue/50 text-lg"></i>
                    </div>
                    <input
                      type="text"
                      placeholder="Enter tracking number or waybill..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="w-full pl-16 pr-6 py-5 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md text-lg font-medium placeholder-grey/60"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-blue hover:from-blue/90 hover:to-blue text-white py-5 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-3"
                  >
                    <i className={`fas ${isLoading ? 'fa-spinner fa-spin' : 'fa-search'} text-lg`}></i>
                    {isLoading ? 'Searching...' : 'Track Shipment'}
                  </button>
                </form>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={openFAQ}
                    className="group w-full flex items-center justify-center gap-2 text-blue hover:text-blue/80 font-semibold transition-colors duration-300"
                  >
                    <i className="fas fa-question-circle group-hover:animate-pulse"></i>
                    Having trouble? Get help with FAQ
                  </button>
                </div>
              </div>
            </div>

            {/* 2) Tracking Number Format — MOBILE ONLY here (between search and info cards) */}
            <div className="order-2 lg:hidden">
              <div className="bg-blue/5 backdrop-blur-sm rounded-3xl p-6 border border-blue/10 h-full">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-info-circle text-blue text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-blue mb-2">Search Options</h3>
                    <p className="text-grey leading-relaxed">
                      You can search using your <strong>tracking number</strong>, <strong>master waybill</strong>, or <strong>house waybill</strong>. These usually include letters and numbers (e.g.,{' '}
                      <span className="font-bold text-blue font-mono bg-blue/10 px-2 py-1 rounded">RCS12345</span>,{' '}
                      <span className="font-bold text-blue font-mono bg-blue/10 px-2 py-1 rounded">MWB123456</span>, or{' '}
                      <span className="font-bold text-blue font-mono bg-blue/10 px-2 py-1 rounded">HWB123456</span>). If you see no results, double-check for typos or extra spaces and try again.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 3) Info / Global Coverage — third on mobile, second column on desktop */}
            <div className="order-3 lg:order-2">
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 md:p-10 h-full flex flex-col">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-blue/5 rounded-2xl border border-blue/10">
                    <div className="w-10 h-10 bg-blue/10 rounded-xl flex items-center justify-center">
                      <i className="fas fa-globe text-blue text-sm"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-blue text-sm">Global Coverage</p>
                      <p className="text-grey text-xs">Reliable shipping globally</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-yellow/5 rounded-2xl border border-yellow/10">
                    <div className="w-10 h-10 bg-yellow/10 rounded-xl flex items-center justify-center">
                      <i className="fas fa-clock text-yellow text-sm"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-blue text-sm">Real-Time Tracking</p>
                      <p className="text-grey text-xs">Live updates on your shipment status</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-2xl border border-green-200">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      <i className="fas fa-shield-alt text-green-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-blue text-sm">Secure & Safe</p>
                      <p className="text-grey text-xs">Your packages are in trusted hands</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-2xl border border-purple-200">
                    <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                      <i className="fas fa-users text-purple-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-blue text-sm">Expert Support</p>
                      <p className="text-grey text-xs">Professional customer service team</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Helper Text — DESKTOP ONLY (original placement retained) */}
          <div className="hidden lg:block mx-auto mb-16">
            <div className="bg-blue/5 backdrop-blur-sm rounded-2xl p-6 border border-blue/10">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="fas fa-info-circle text-blue text-lg"></i>
                </div>
                <div>
                  <h3 className="font-bold text-blue mb-2">Search Options</h3>
                  <p className="text-grey leading-relaxed">
                    You can search using your <strong>tracking number</strong>, <strong>master waybill</strong>, or <strong>house waybill</strong>. These usually include letters and numbers (e.g.,{' '}
                    <span className="font-bold text-blue font-mono bg-blue/10 px-2 py-1 rounded">RCS12345</span>,{' '}
                    <span className="font-bold text-blue font-mono bg-blue/10 px-2 py-1 rounded">MWB123456</span>, or{' '}
                    <span className="font-bold text-blue font-mono bg-blue/10 px-2 py-1 rounded">HWB123456</span>). If you see no results, double-check for typos or extra spaces and try again.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature cards (unchanged) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* cards... */}
          </div>
        </div>
      </section>

      {/* FAQ Modal */}
      {showFAQ && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl w-full max-w-3xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-blue px-8 py-6 relative">
              <button
                onClick={closeFAQ}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center text-white transition-all duration-200"
              >
                <i className="fas fa-times text-lg"></i>
              </button>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <i className="fas fa-question-circle text-white text-2xl"></i>
                </div>
                <div>
                  <h2 className="text-white font-bold text-2xl mb-1">Frequently Asked Questions</h2>
                  <p className="text-white/80 text-sm">Find quick answers to common questions</p>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-8">
              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-200">
                    <button
                      onClick={() => toggleFAQ(idx)}
                      className="w-full flex justify-between items-center p-6 text-left font-semibold text-lg hover:bg-blue/5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue/20 focus:ring-inset"
                    >
                      <span className="text-blue pr-4">{faq.question}</span>
                      <div
                        className={`w-8 h-8 bg-blue/10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                          activeIndex === idx ? 'rotate-180 bg-blue text-white' : ''
                        }`}
                      >
                        <i className="fas fa-chevron-down text-sm"></i>
                      </div>
                    </button>
                    {activeIndex === idx && (
                      <div className="px-6 pb-6">
                        <div className="bg-white rounded-xl p-4 border border-gray-100">
                          <p className="text-grey leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 px-8 py-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
                <div className="text-grey text-sm">
                  Still need help? Contact our support team anytime.
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={closeFAQ}
                    className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-grey font-medium rounded-xl transition-all duration-200 hover:shadow-md"
                  >
                    Close
                  </button>
                  <a
                    href="https://www.rcslogisticsph.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-2 bg-blue hover:from-blue/90 hover:to-blue text-white font-medium rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg flex items-center gap-2"
                  >
                    <i className="fas fa-external-link-alt text-sm"></i>
                    Contact Support
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default TrackAndTrace;
